import React, { useState, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { CATALOG } from '../data/catalog';
import { computeBbox, isSelfIntersecting } from '../utils/shopGeometry';
import { aiFetch } from '../utils/aiFetch';

// Limits used by validators. Easy to tune.
const MIN_DIM_FT = 10;
const MAX_DIM_FT = 500;
const MIN_WALL_FT = 1;
const MAX_WALL_FT = 500;

async function analyzeSketch(base64Image, mediaType, getToken) {
  const prompt = `You are ShopBuilder's sketch analysis system — an expert at reading automotive shop floor plans and converting them to structured data. Never refer to yourself as Claude or AI. You are ShopBuilder.

STEP 1 — VALIDATE THE IMAGE:
First, look at the image. Decide if it actually appears to be a hand-drawn or digital sketch of an automotive shop floor plan. Valid: rectangles or polygons representing shops, labeled lifts, doors, equipment, dimensions. Invalid: photos of food, animals, landscapes, screenshots of websites, random doodles, blank images.

If the image is NOT a shop layout sketch, respond with EXACTLY this JSON and nothing else:
{
  "valid": false,
  "reason": "<one short friendly sentence — e.g. 'ShopBuilder makes shops, not sandwiches 😄 Try uploading a shop floor plan sketch.'>"
}

STEP 2 — DETERMINE SHAPE:
Look at the OUTLINE of the shop. Choose ONE of:
- "rect" — the shop outline is a simple rectangle (4 right-angle corners). USE THIS BY DEFAULT. A slightly wonky hand-drawn rectangle is still a rectangle.
- "polygon" — the shop outline is clearly NOT a rectangle (L-shape, T-shape, has notches, has angled walls, has 5+ corners). ONLY use this when the non-rectangular shape is unmistakable.

When in doubt, pick "rect". A bad rect is still usable; a wrong polygon is not.

STEP 3 — IF VALID, ANALYZE THE FLOOR PLAN:
Map equipment to these IDs:
- "2post"     = anything labeled 2-POST LIFT, two-post lift, 2 post (default 10x16 ft footprint)
- "4post"     = anything labeled 4-POST LIFT, four-post lift (default 16x24 ft)
- "scissor"   = anything labeled SCISSOR LIFT, scissor, low rise (default 12x22 ft)
- "tc"        = TIRE CHANGER, tire machine (default 6.2x3.7 ft incl. assist arm)
- "bal"       = WHEEL BALANCER, balancer (default 4.3x4.8 ft incl. hood)
- "aln"       = WHEEL ALIGNER, alignment machine (default 8.75x3 ft console + camera beam)
- "comp"      = AIR COMPRESSOR, compressor (default 3.2x2.2 ft)
- "wash"      = PARTS WASHER (default 2.5x1.75 ft)
- "fjack"     = FLOOR JACK (default 1.2x2.7 ft)
- "odrain"    = OIL DRAIN, oil drain station (default 1.8x3.8 ft)
- "toolbox"   = TOOL CABINET, tool box (default 6x2 ft)
- "wbench"    = WORKBENCH, work bench (default 6x4 ft)
- "obstruction" = PILLAR, COLUMN, POST, BEAM (default 2x2 ft)

For cx and cy: estimate the CENTER position of each item in feet from the TOP-LEFT (NW) corner of the shop's bounding box.

For office (rect shape only — leave null for polygon): if there is an enclosed room or office area in a corner, identify which corner (NW, NE, SW, SE) and estimate width and depth in feet.

RESPONSE FORMAT — pick ONE of these two shapes:

==== If shape is "rect": ====
{
  "shape": "rect",
  "shop": { "w": <width as number>, "h": <depth as number>, "unit": "ft" },
  "doors": [
    { "wall": "N|S|E|W", "pos": 0.0-1.0, "type": "overhead|door", "width": 10 }
  ],
  "office": { "corner": "NW|NE|SW|SE", "w": 12, "h": 10 } or null,
  "equipment": [
    { "id": "equipment_id", "cx": 20, "cy": 15, "rot": 0, "scaleX": 1, "scaleY": 1, "label": "" }
  ]
}

==== If shape is "polygon": ====
{
  "shape": "polygon",
  "vertices": [
    { "x": 0, "y": 0 },
    { "x": 60, "y": 0 },
    { "x": 60, "y": 30 },
    { "x": 30, "y": 30 },
    { "x": 30, "y": 50 },
    { "x": 0, "y": 50 }
  ],
  "doors": [
    { "wallIndex": 0, "pctAlong": 0.5, "type": "overhead|door", "width": 10 }
  ],
  "office": null,
  "equipment": [
    { "id": "equipment_id", "cx": 20, "cy": 15, "rot": 0, "scaleX": 1, "scaleY": 1, "label": "" }
  ]
}

POLYGON RULES (critical):
- All coordinates in FEET, not pixels.
- Vertices listed in CLOCKWISE order, starting from the top-left (NW-most) corner.
- The NW-most vertex MUST be at (0, 0). Translate all vertices so this is true.
- Minimum 3 vertices, maximum 20.
- Every wall must be at least 1 ft long. Walls run from vertices[i] to vertices[(i+1) % length].
- "wallIndex" in doors refers to the wall starting at vertices[wallIndex]. So wallIndex 0 = first wall (vertices[0] → vertices[1]).
- "pctAlong" is 0.0 to 1.0 along the wall in vertex order (0 = at start vertex, 1 = at end vertex).
- The polygon must not self-intersect.

Return ONLY the JSON, no explanation, no markdown, no backticks.`;

  // aiFetch attaches Clerk auth token and handles 401/403 with friendly errors
  const data = await aiFetch({
    model: 'claude-sonnet-5',
    max_tokens: 2000,
    messages: [{
      role: 'user',
      content: [
        {
          type: 'image',
          source: { type: 'base64', media_type: mediaType, data: base64Image },
        },
        { type: 'text', text: prompt },
      ],
    }],
  }, getToken);

  const text = data.content?.[0]?.text || '';
  console.log('Claude raw response:', text);

  // Strip any accidental markdown fences, then locate the JSON object
  const clean = text.replace(/```json|```/g, '').trim();
  const jsonStart = clean.indexOf('{');
  const jsonEnd = clean.lastIndexOf('}');
  if (jsonStart === -1 || jsonEnd === -1) {
    throw new Error("ShopBuilder couldn't read this image. Try uploading a clearer floor plan sketch.");
  }
  const parsed = JSON.parse(clean.slice(jsonStart, jsonEnd + 1));

  // Reject if the image wasn't recognized as a shop sketch
  if (parsed.valid === false) {
    throw new Error(parsed.reason || "ShopBuilder makes shops, not sandwiches 😄 Try uploading a shop floor plan sketch.");
  }

  // Equipment IDs we accept — drop unknown items so layouts don't break
  const VALID_EQ_IDS = ['2post','4post','scissor','inground','mobile_column','tc','bal','aln','comp','wash','fjack','odrain','toolbox','wbench','obstruction'];

  // Branch on shape. Default to 'rect' if missing for backwards compatibility.
  const shape = parsed.shape === 'polygon' ? 'polygon' : 'rect';

  if (shape === 'polygon') {
    // Validate the polygon. On any failure, fall back to using the bbox as a
    // rect so the user still gets a usable result rather than an error.
    const polyResult = validatePolygonResponse(parsed);
    if (polyResult.ok) {
      if (Array.isArray(parsed.equipment)) {
        parsed.equipment = parsed.equipment.filter(p => VALID_EQ_IDS.includes(p.id)).slice(0, 50);
      }
      if (Array.isArray(parsed.doors)) {
        parsed.doors = parsed.doors.slice(0, 10);
      }
      return { ...parsed, shape: 'polygon' };
    }
    // Polygon was invalid — fall back to bbox dimensions as a rectangle
    console.warn('Polygon validation failed:', polyResult.reason, '— falling back to bbox rect');
    const bbox = polyResult.bbox;
    const fallback = {
      shape: 'rect',
      shop: { w: bbox.w, h: bbox.h, unit: 'ft' },
      doors: [],   // we don't try to remap polygon doors → rect doors; user re-adds
      office: null,
      equipment: Array.isArray(parsed.equipment)
        ? parsed.equipment.filter(p => VALID_EQ_IDS.includes(p.id)).slice(0, 50)
        : [],
      _polygonFallbackReason: polyResult.reason,   // surfaced in preview UI
    };
    if (fallback.shop.w < MIN_DIM_FT || fallback.shop.w > MAX_DIM_FT ||
        fallback.shop.h < MIN_DIM_FT || fallback.shop.h > MAX_DIM_FT) {
      throw new Error("Shop dimensions look unusual. Try a sketch with clearer measurements (10-500 ft).");
    }
    return fallback;
  }

  // ---- RECT PATH (unchanged from original behavior) ----
  if (!parsed.shop || typeof parsed.shop.w !== 'number' || typeof parsed.shop.h !== 'number') {
    throw new Error("ShopBuilder couldn't extract shop dimensions from this sketch. Try a clearer image.");
  }
  if (parsed.shop.w < MIN_DIM_FT || parsed.shop.w > MAX_DIM_FT ||
      parsed.shop.h < MIN_DIM_FT || parsed.shop.h > MAX_DIM_FT) {
    throw new Error("Shop dimensions look unusual. Try a sketch with clearer measurements (10-500 ft).");
  }
  if (Array.isArray(parsed.equipment)) {
    parsed.equipment = parsed.equipment.filter(p => VALID_EQ_IDS.includes(p.id)).slice(0, 50);
  }
  if (Array.isArray(parsed.doors)) {
    parsed.doors = parsed.doors.slice(0, 10);
  }
  return { ...parsed, shape: 'rect' };
}

// Validate a polygon response from Claude. Returns either { ok: true } for use,
// or { ok: false, reason, bbox } for fallback. Also normalizes vertex coords
// in-place so the NW-most vertex is at origin (defensive — the prompt asks
// for this but we don't trust the model to comply 100% of the time).
function validatePolygonResponse(parsed) {
  const verts = parsed.vertices;
  if (!Array.isArray(verts) || verts.length < 3) {
    return { ok: false, reason: 'fewer than 3 vertices', bbox: { w: 40, h: 30 } };
  }
  if (verts.length > 20) {
    return { ok: false, reason: 'too many vertices', bbox: computeBbox(verts) };
  }
  for (const v of verts) {
    if (typeof v?.x !== 'number' || typeof v?.y !== 'number' ||
        !isFinite(v.x) || !isFinite(v.y)) {
      return { ok: false, reason: 'non-numeric vertex', bbox: { w: 40, h: 30 } };
    }
  }

  // Normalize: shift so bbox NW is (0, 0). Round to 0.1 ft (matches makeFreeShop convention).
  const bbox = computeBbox(verts);
  for (const v of verts) {
    v.x = Math.round((v.x - bbox.minX) * 10) / 10;
    v.y = Math.round((v.y - bbox.minY) * 10) / 10;
  }
  const finalBbox = computeBbox(verts);

  // Bbox sanity — same bounds as rect path
  if (finalBbox.w < MIN_DIM_FT || finalBbox.w > MAX_DIM_FT ||
      finalBbox.h < MIN_DIM_FT || finalBbox.h > MAX_DIM_FT) {
    return { ok: false, reason: 'shop dimensions out of range', bbox: finalBbox };
  }

  // Wall-length check — catches AI returning pixel coords by accident, or
  // tiny zero-length edges from duplicate vertices
  for (let i = 0; i < verts.length; i++) {
    const a = verts[i], b = verts[(i + 1) % verts.length];
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    if (len < MIN_WALL_FT || len > MAX_WALL_FT) {
      return { ok: false, reason: `wall ${i} length ${len.toFixed(1)} ft is out of range`, bbox: finalBbox };
    }
  }

  if (isSelfIntersecting(verts)) {
    return { ok: false, reason: 'polygon self-intersects', bbox: finalBbox };
  }

  // Validate door wallIndex references — drop bad ones rather than failing import
  if (Array.isArray(parsed.doors)) {
    parsed.doors = parsed.doors.filter(d =>
      typeof d.wallIndex === 'number' && d.wallIndex >= 0 && d.wallIndex < verts.length
    );
  }

  return { ok: true };
}

// Convert a polygon-format door { wallIndex, pctAlong, type, width } into
// the wall-attached format { wallId, offset, width, type } that shopGeometry
// expects. Done here in the import layer so App.js can use the doors directly.
//
// Note: in verticesToWalls(), wall.id === vertex index, so wallIndex maps
// straight to wallId. If that ever changes in shopGeometry, this will break —
// hence the comment.
function polygonDoorToWallDoor(d, vertices) {
  const i = d.wallIndex;
  const a = vertices[i];
  const b = vertices[(i + 1) % vertices.length];
  const wallLen = Math.hypot(b.x - a.x, b.y - a.y);
  const type = d.type === 'overhead' ? 'overhead' : 'door';
  const width = Number(d.width) || (type === 'overhead' ? 10 : 4);
  // Center the door on pctAlong; offset is door START distance from wall start.
  // Use ?? not || here — pctAlong=0 is valid (door at very start of wall) but
  // would falsy-default to 0.5 with ||.
  const pctRaw = Number(d.pctAlong);
  const pct = isFinite(pctRaw) ? Math.max(0, Math.min(1, pctRaw)) : 0.5;
  const center = pct * wallLen;
  const offset = Math.max(0, Math.min(wallLen - width, center - width / 2));
  return {
    id: Date.now() + Math.random(),
    wallId: i,
    offset,
    width,
    type,
  };
}

// Downscales + re-encodes an image before we base64 it and POST to
// api/anthropic.js. Vercel serverless functions hard-cap request bodies at
// 4.5MB (platform-level, not configurable via vercel.json) — phone photos
// of sketches routinely run 3-8MB, and base64 adds ~33% overhead on top of
// that, so uncompressed uploads blow past the limit before Anthropic (or
// even our own function code) ever sees the request. This is what fixes
// the "413 FUNCTION_PAYLOAD_TOO_LARGE" error.
function compressImage(file, maxDimension = 1600, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src); // free the temp object URL once decoded

      // Scale proportionally so the longest side fits maxDimension
      const scale = Math.min(1, maxDimension / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);

      // Re-encode as JPEG — much smaller than a raw camera photo, and
      // consistent regardless of the source file type (png/heic/etc).
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Image compression failed'))),
        'image/jpeg',
        quality
      );
    };
    img.onerror = () => reject(new Error('Could not read this image file'));
    img.src = URL.createObjectURL(file);
  });
}

export default function SketchImport({ onImport, onClose }) {
  const { getToken } = useAuth(); // Clerk session token for AI API calls
  const [stage, setStage] = useState('upload');
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setError('');
    setStage('analyzing'); // show the spinner right away — compression takes a beat too

    try {
      // Shrink/re-encode first so the upload stays under Vercel's 4.5MB cap
      const compressed = await compressImage(file);
      const mediaType = 'image/jpeg'; // canvas.toBlob above always outputs jpeg

      // Read the compressed blob as a data URL — gives us both the preview
      // image and the base64 payload from one read
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = () => reject(new Error('Could not read the compressed image'));
        reader.readAsDataURL(compressed);
      });

      setPreview(dataUrl);

      const base64 = dataUrl.split(',')[1];
      const layout = await analyzeSketch(base64, mediaType, getToken);
      console.log('Parsed layout:', layout);
      setResult(layout);
      setStage('preview');
    } catch (err) {
      console.error('Sketch analysis failed:', err);
      setError(err.message || 'Could not read the sketch. Try a clearer image with labeled dimensions and equipment.');
      setStage('error');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleApply = () => {
    if (!result) return;

    // Build placed[] — same logic for both rect and polygon shapes
    const placed = (result.equipment || []).map(eq => {
      const catalogItem = CATALOG.find(c => c.id === eq.id);
      if (!catalogItem) return null;
      return {
        ...JSON.parse(JSON.stringify(catalogItem)),
        uid: `${Date.now()}-${Math.random()}`,
        cx: Math.max(1, Number(eq.cx) || 10),
        cy: Math.max(1, Number(eq.cy) || 10),
        rot: Number(eq.rot) || 0,
        scaleX: Number(eq.scaleX) || 1,
        scaleY: Number(eq.scaleY) || 1,
        label: eq.label || '',
        _dragging: false,
      };
    }).filter(Boolean);

    if (result.shape === 'polygon') {
      // Polygon path — emit pre-migrated doors and the polygon shape descriptor.
      // App.js detects the `shape: 'polygon'` flag and routes accordingly.
      const wallDoors = (result.doors || []).map(d => polygonDoorToWallDoor(d, result.vertices));
      onImport({
        shape: 'polygon',
        vertices: result.vertices,
        doors: wallDoors,
        office: null,
        placed,
      });
    } else {
      // Rect path — emit legacy payload, App.js migrates doors itself.
      // This preserves the existing contract used by PromptImport too.
      const doors = (result.doors || []).map((d, i) => ({
        id: Date.now() + i,
        wall: ['N', 'S', 'E', 'W'].includes(d.wall) ? d.wall : 'S',
        pos: Math.max(0.05, Math.min(0.95, Number(d.pos) || 0.5)),
        type: d.type === 'overhead' ? 'overhead' : 'door',
        width: Number(d.width) || (d.type === 'overhead' ? 10 : 4),
      }));
      onImport({
        shop: {
          w: Number(result.shop?.w) || 40,
          h: Number(result.shop?.h) || 30,
          unit: 'ft',
        },
        doors,
        office: result.office || null,
        placed,
      });
    }
    onClose();
  };

  // Helper for the preview readout — handles both shapes
  const shopSizeLabel = result
    ? (result.shape === 'polygon'
        ? `${result.vertices?.length || 0}-sided polygon (${Math.round(computeBbox(result.vertices || []).w)} × ${Math.round(computeBbox(result.vertices || []).h)} ft bbox)`
        : `${result.shop?.w || '?'} × ${result.shop?.h || '?'} ft`)
    : '';

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 24,
    }}>
      <div style={{
        background: '#0a1628', border: '1px solid #162840', borderRadius: 14,
        width: '100%', maxWidth: 580, maxHeight: '90vh', overflow: 'auto',
        boxShadow: '0 8px 48px rgba(0,0,0,.7)',
      }}>

        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #162840', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#e8f4ff', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: '#aa66ff' }}>✦</span> AI Sketch Import
            </div>
            <div style={{ fontSize: 11, color: '#4a7a9b', marginTop: 3, lineHeight: 1.5 }}>
              Upload a floor plan or sketch — Shopbuilder will interpret the layout automatically
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#4a7a9b', fontSize: 20, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ padding: '20px 24px' }}>

          {/* UPLOAD */}
          {stage === 'upload' && (
            <div
              onDrop={handleDrop}
              onDragOver={e => e.preventDefault()}
              onClick={() => fileRef.current?.click()}
              style={{
                border: '2px dashed #1e3a54', borderRadius: 10,
                padding: '48px 24px', textAlign: 'center', cursor: 'pointer',
                background: 'rgba(0,80,180,.04)',
              }}
            >
              <div style={{ fontSize: 44, marginBottom: 14 }}>📐</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#e8f4ff', marginBottom: 8 }}>
                Drop your floor plan here
              </div>
              <div style={{ fontSize: 12, color: '#4a7a9b', lineHeight: 1.7, marginBottom: 20 }}>
                Works best with labeled floor plans showing dimensions,<br />
                door types (OVR/WLK), and equipment names.<br />
                Hand-drawn sketches and L-shapes both work.
              </div>
              <div style={{ display: 'inline-block', background: 'linear-gradient(135deg,#5500aa,#8833ff)', borderRadius: 7, color: '#fff', padding: '9px 24px', fontSize: 13, fontWeight: 600 }}>
                Choose Image
              </div>
              <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
                onChange={e => handleFile(e.target.files[0])} />
            </div>
          )}

          {/* ANALYZING */}
          {stage === 'analyzing' && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              {preview && (
                <img src={preview} alt="sketch" style={{
                  maxWidth: '100%', maxHeight: 200, borderRadius: 8,
                  marginBottom: 24, border: '1px solid #162840', objectFit: 'contain',
                }} />
              )}
              <div style={{ fontSize: 13, color: '#aa66ff', fontWeight: 600, marginBottom: 8 }}>
                Analyzing your floor plan...
              </div>
              <div style={{ fontSize: 11, color: '#4a7a9b', lineHeight: 1.6, marginBottom: 20 }}>
                Shopbuilder is reading dimensions, doors, and equipment positions.
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 6 }}>
                {[0, 1, 2].map(i => (
                  <div key={i} style={{
                    width: 8, height: 8, borderRadius: '50%', background: '#aa66ff',
                    animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite`,
                  }} />
                ))}
              </div>
              <style>{`
                @keyframes pulse {
                  0%, 100% { opacity: 0.3; transform: scale(0.8); }
                  50% { opacity: 1; transform: scale(1.2); }
                }
              `}</style>
            </div>
          )}

          {/* PREVIEW */}
          {stage === 'preview' && result && (
            <div>
              {preview && (
                <img src={preview} alt="sketch" style={{
                  width: '100%', maxHeight: 160, borderRadius: 8,
                  marginBottom: 16, border: '1px solid #162840', objectFit: 'contain',
                }} />
              )}

              <div style={{ fontSize: 10, fontWeight: 700, color: '#00cc88', marginBottom: 10, letterSpacing: '.08em', textTransform: 'uppercase' }}>
                ✓ Layout Detected
              </div>

              {/* Polygon-fallback warning — shows when AI returned an unusable polygon */}
              {result._polygonFallbackReason && (
                <div style={{ padding: '8px 10px', background: 'rgba(255,170,0,.08)', border: '1px solid #ff990033', borderRadius: 6, fontSize: 10, color: '#ffaa44', marginBottom: 10, lineHeight: 1.5 }}>
                  <strong>Note:</strong> Detected a complex shape but couldn't read it cleanly ({result._polygonFallbackReason}). Using the bounding rectangle instead — you can redraw the shape in Setup.
                </div>
              )}

              {[
                { label: 'Shop Size', value: shopSizeLabel },
                { label: 'Office', value: result.office ? `${result.office.corner} corner (${result.office.w} × ${result.office.h} ft)` : (result.shape === 'polygon' ? 'Skipped for polygon shapes' : 'None detected') },
                { label: 'Doors', value: `${result.doors?.length || 0} detected (${result.doors?.filter(d => d.type === 'overhead').length || 0} overhead)` },
                { label: 'Equipment', value: `${result.equipment?.length || 0} items detected` },
              ].map(({ label, value }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #0d1e30', fontSize: 11 }}>
                  <span style={{ color: '#4a7a9b', fontWeight: 600 }}>{label}</span>
                  <span style={{ color: '#c8dff0' }}>{value}</span>
                </div>
              ))}

              {/* Equipment detail */}
              {(result.equipment || []).length > 0 && (
                <div style={{ marginTop: 12, marginBottom: 12 }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: '#4a7a9b', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 6 }}>
                    Equipment
                  </div>
                  {(result.equipment || []).map((eq, i) => {
                    const cat = CATALOG.find(c => c.id === eq.id);
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: '1px solid #0d1e30', fontSize: 11 }}>
                        <div style={{ width: 8, height: 8, borderRadius: 2, background: cat?.border || '#888', flexShrink: 0 }} />
                        <span style={{ color: '#c8dff0', flex: 1 }}>{cat?.name || eq.id}</span>
                        <span style={{ color: '#4a7a9b', fontFamily: 'monospace', fontSize: 10 }}>
                          @ {Number(eq.cx || 0).toFixed(0)}, {Number(eq.cy || 0).toFixed(0)} ft
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              <div style={{ padding: '10px 12px', background: 'rgba(255,170,0,.07)', border: '1px solid #ff990033', borderRadius: 6, fontSize: 11, color: '#ffaa44', marginBottom: 16, lineHeight: 1.6 }}>
                <strong>Review before applying.</strong> Positions are estimated — adjust anything in the editor after import.
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => { setStage('upload'); setPreview(null); setResult(null); }} style={btnS}>
                  ← Try Again
                </button>
                <button onClick={handleApply} style={{
                  flex: 1, background: 'linear-gradient(135deg,#5500aa,#8833ff)',
                  border: 'none', borderRadius: 8, color: '#fff',
                  padding: 11, fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}>
                  Apply Layout →
                </button>
              </div>
            </div>
          )}

          {/* ERROR */}
          {stage === 'error' && (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              {preview && (
                <img src={preview} alt="sketch" style={{
                  maxWidth: '100%', maxHeight: 150, borderRadius: 8,
                  marginBottom: 20, border: '1px solid #162840', objectFit: 'contain',
                }} />
              )}
              <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#ff6644', marginBottom: 8 }}>
                Could not interpret floor plan
              </div>
              <div style={{ fontSize: 11, color: '#4a7a9b', lineHeight: 1.7, marginBottom: 16 }}>{error}</div>
              <div style={{ padding: '12px 14px', background: '#07111e', border: '1px solid #162840', borderRadius: 6, marginBottom: 20, textAlign: 'left', lineHeight: 1.8 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#c8dff0', marginBottom: 6 }}>Tips for best results:</div>
                {[
                  'Label shop dimensions clearly (e.g. "40 ft × 30 ft")',
                  'Mark doors as OVR (overhead) or WLK (walk door)',
                  'Label equipment by name (e.g. "2-POST LIFT")',
                  'Use a clear overhead/top-down view',
                  'Good contrast between lines and background',
                ].map((tip, i) => (
                  <div key={i} style={{ fontSize: 10, color: '#4a7a9b' }}>• {tip}</div>
                ))}
              </div>
              <button
                onClick={() => { setStage('upload'); setPreview(null); setError(''); }}
                style={{ ...btnS, width: '100%', padding: 11 }}>
                Try Another Image
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const btnS = {
  background: 'transparent', border: '1px solid #1e3a54',
  borderRadius: 8, color: '#6a9fc0', fontSize: 12,
  fontWeight: 500, padding: '10px 18px',
  cursor: 'pointer', fontFamily: 'inherit',
};
